import { useMemo } from 'react';

import { Novo } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Novo';
import { isWeb } from '@tamagui/core';
import { LinearGradient as TamaguiLinearGradient } from '@tamagui/linear-gradient';
import type { LinearGradientProps as TamaguiLinearGradientProps } from '@tamagui/linear-gradient';

const { gradient: gradientTokens } = Novo;

export type LinearGradientProps = TamaguiLinearGradientProps & {
  /**
   * Token of the gradient to use
   */
  gradient: `$${keyof typeof gradientTokens}`;
};

/**
 * Have to remove the $ because we're not actually using Tamagui's token system
 * for gradient tokens yet. Each gradient "token" that comes back from
 * tokens-studio is actually a complex object - maybe in the future we can
 * set it up to work more like typography or shadows.
 */
const removeDollarSign = (str: LinearGradientProps['gradient']): keyof typeof gradientTokens =>
  str.slice(1) as keyof typeof gradientTokens;

/**
 * Renders a linear gradient using a design system token. Only used internally
 * for now.
 */
export const LinearGradient = ({ gradient, ...props }: LinearGradientProps) => {
  const { colors, locations, start, end } = useMemo(() => {
    const { colorStops, direction } = gradientTokens[removeDollarSign(gradient)];

    // Make sure color stops are in order. Web doesn't seem to like it when
    // they're not. This might not be necessary if we update all the tokens
    const sortedColorStops = [...colorStops].sort((cs1, cs2) => cs1.stop - cs2.stop);

    const colors = [];
    const locations = [];

    for (const colorStop of sortedColorStops) {
      colors.push(colorStop.color);
      locations.push(colorStop.stop);
    }

    const start = { x: 0, y: 0 };

    // Convert degress to coordinates
    const degreesFromVertical = parseInt(direction, 10) - 90;
    const radians = degreesFromVertical * (Math.PI / 180);
    const end = {
      x: Math.round(Math.cos(radians) * 1000) / 1000,
      y: Math.round(Math.sin(radians) * 1000) / 1000,
    };

    // Make sure end values are all positive. Native seems to require this
    if (end.x < 0) {
      start.x = start.x + 1;
      end.x = end.x + 1;
    }
    if (end.y < 0) {
      start.y = start.y + 1;
      end.y = end.y + 1;
    }

    return { colors, locations, start, end };
  }, [gradient]);

  return (
    <TamaguiLinearGradient
      colors={colors}
      locations={locations}
      // Tamagui introduced a bug where now the angle for the CSS gradient is
      // only calculated correctly when start and end are passed as arrays,
      // but native still requires them as objects.
      start={isWeb ? [start.x, start.y] : start}
      end={isWeb ? [end.x, end.y] : end}
      {...props}
    />
  );
};
